import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faPencil, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useAuth, CustomDropdown, useNotifications, CustomDatePicker, calendarDateFromInstant, calendarDateToEpochMillis } from '@medprompt/shared';
import { UserAbility } from '@medprompt/shared';
import { Significance, getNextSignificance, getSignificanceConfig } from '../../enums/significanceEnum';
import { EmptyStateMessage } from './Tile';

const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  
  try {
    const date = new Date(timestamp);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // month is 0-indexed
    const year = date.getFullYear();
    
    return `${day}/${month}/${year}`;
  } catch (error) {
    console.error('Error formatting timestamp:', error);
    return '';
  }
};

// Reusable editable content component
export const EditableContent = React.memo(({
  title,
  isEditing, 
  onEdit, 
  onSave, 
  onDiscard,
  renderDisplay,
  renderEditor,
  isEditingEnabled
}) => {
  const { user } = useAuth();
  const hasDocsWriteAbility = user?.authorities?.includes(UserAbility.DOCS_WRITE.key);
  // Use the passed prop if available, otherwise fall back to the user permission check
  const canEdit = isEditingEnabled !== undefined ? isEditingEnabled() : hasDocsWriteAbility;

  return (
    <>
      {(isEditing || canEdit) && (
        <div className="edit-controls">
          {isEditing ? (
            <>
              <button className="button document-save-button" onClick={onSave}>Save</button>
              <button className="button discard-button" onClick={onDiscard}>Discard</button>
            </>
          ) : (
            <button className="button edit-button" onClick={onEdit}>
              <FontAwesomeIcon icon={faPencil} />
              {title && <span style={{ marginLeft: '8px' }}>{title}</span>}
            </button>
          )}
        </div>
      )}
      {isEditing ? renderEditor() : renderDisplay()}
    </>
  );
});

// Reusable code item component
export const CodeItem = React.memo(({ code, index, idPrefix, onCodeSelectionChange, onSignificanceChange, onDurationChange, onDescriptionEdit, onDescriptionSave, editingCodeId, editableDescription, setEditableDescription, canEdit, durationDropdownOpen, toggleDurationDropdown, getDurationDescription, getDurationOptions, canEditDuration = true, shouldEnableSignificanceToggle = true, qofContent = null, onQuoteClick = null, onDelete = null, onDateChange = null, highlighted = false }) => {
  const { addNotification } = useNotifications();

  const handleCopyToClipboard = (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => {
        addNotification(`copy-success-${Date.now()}`, 'Copied to clipboard', { isError: false });
      }).catch(() => {
        addNotification(`copy-error-${Date.now()}`, 'Failed to copy to clipboard', { isError: true });
      });
    } else {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        addNotification(`copy-success-${Date.now()}`, 'Copied to clipboard', { isError: false });
      } catch (err) {
        addNotification(`copy-error-${Date.now()}`, 'Failed to copy to clipboard', { isError: true });
      }
      document.body.removeChild(textArea);
    }
  };

  const getSignificanceTooltip = (significance) => {
    return getSignificanceConfig(significance).getTooltip();
  };

  const handleSignificanceClick = () => {
    if (canEdit && onSignificanceChange && shouldEnableSignificanceToggle) {
      const newSignificance = getNextSignificance(code.significance);
      onSignificanceChange(code.codeId, newSignificance);
    }
  };

  const handleDateChange = (calendarDateStr) => {
    if (!onDateChange) return;
    const epochMillis = calendarDateStr ? calendarDateToEpochMillis(calendarDateStr) : null;
    onDateChange(code.codeId, epochMillis);
  };

  const dateCalendarValue = code.date ? calendarDateFromInstant(code.date) : null;

  const significanceLabel = code.significance
    ? code.significance.charAt(0).toUpperCase() + code.significance.slice(1).toLowerCase()
    : null;

  return (
    <div
      className={`document-ai-item${code.significance ? ` significance-${code.significance.toLowerCase()}` : ''}${highlighted ? ' ai-item-highlight' : ''}`}
      id="ai-code-item"
      data-existing-code-id={code.codeId}
    >
      {canEdit && onDelete && (
        <button
          type="button"
          className="code-delete-button code-item-delete-button"
          onClick={() => onDelete(code.codeId)}
          title="Remove code"
          aria-label="Remove code"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      )}
      <div className="code-details">
        <div className="code-header">
          <span className="code-id">({code.code.code})</span>
          {qofContent}
          {(code.timestamp || code.date) && (
            <span className="code-timestamp">{formatTimestamp(code.timestamp || code.date)}</span>
          )}
        </div>

        <div className="code-description">
          {code.code.terms[0]}
          {code.code.terms.length > 1 && (
            <div className="code-terms-overlay">
              {code.code.terms.slice(1, Math.min(code.code.terms.length, 4)).map((term, termIndex) => (
                <div key={termIndex} className="code-term">{term}</div>
              ))}
            </div>
          )}
        </div>

        {/* Source quote from summary */}
        {code.quote && (
          <div className="code-quote">
            <span
              className="code-quote-copy-icon"
              onClick={(e) => { e.stopPropagation(); handleCopyToClipboard(code.quote); }}
              title="Copy quote"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCopyToClipboard(code.quote);
                }
              }}
            >
              <FontAwesomeIcon icon={faCopy} />
            </span>
            <span
              className={`code-quote-text${onQuoteClick ? ' clickable' : ''}`}
              onClick={() => onQuoteClick && onQuoteClick(code.quote)}
            >
              "{code.quote}"
            </span>
          </div>
        )}

        {/* Comment field – full width */}
        <div className="code-custom-description">
          {editingCodeId === code.codeId ? (
            <textarea
              value={editableDescription}
              onChange={(e) => setEditableDescription && setEditableDescription(e.target.value)}
              onBlur={() => onDescriptionSave && onDescriptionSave(code.codeId, false, code.description)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setEditableDescription(code.description || '');
                  onDescriptionEdit && onDescriptionEdit(code.codeId, code.description);
                }
              }}
              className="description-edit-textarea"
              placeholder="Add a custom description or note for this code..."
              rows="2"
              autoFocus
            />
          ) : (
            <div
              className={`custom-description-text ${canEdit ? 'custom-description-editable' : ''}`}
              onClick={() => canEdit && onDescriptionEdit && onDescriptionEdit(code.codeId, code.description)}
              onMouseMove={(e) => {
                const tooltip = e.currentTarget.querySelector('.edit-tooltip');
                if (tooltip) {
                  tooltip.style.left = `${e.clientX + 10}px`;
                  tooltip.style.top = `${e.clientY + 10}px`;
                }
              }}
            >
              {code.description ? (
                <span className="description-content">{code.description}</span>
              ) : (
                <span className="description-placeholder">Add comment...</span>
              )}
              {canEdit && <div className="edit-tooltip">Click to {code.description ? 'edit' : 'add'} comment</div>}
            </div>
          )}
        </div>

        {/* Bottom action bar */}
        <div className="code-action-bar">
          {/* Significance pill */}
          {shouldEnableSignificanceToggle && significanceLabel && (
            <button
              type="button"
              className="significance-pill"
              onClick={handleSignificanceClick}
              disabled={!canEdit}
            >
              {significanceLabel}
              <div className="significance-tooltip">
                {getSignificanceTooltip(code.significance)}
              </div>
            </button>
          )}

          {/* Duration dropdown */}
          {canEditDuration && code.significance !== Significance.ADMINISTRATIVE.value && getDurationDescription && getDurationOptions && (
            <div className="duration-select-container">
              <CustomDropdown
                selected={getDurationDescription(code.duration || 0)}
                options={getDurationOptions()}
                isOpen={durationDropdownOpen && durationDropdownOpen[code.codeId]}
                toggleDropdown={() => toggleDurationDropdown && toggleDurationDropdown(code.codeId)}
                onSelect={(option) => onDurationChange && onDurationChange(code.codeId, option.value)}
                disabled={!canEdit}
                className="duration-dropdown"
                compact
              />
            </div>
          )}

          {/* Compact date picker */}
          {onDateChange && (
            <CustomDatePicker
              compact
              compactAlign="mid"
              value={dateCalendarValue}
              onChange={handleDateChange}
            />
          )}

          <div className="code-action-bar-right">
            {/* Select checkbox */}
            <input
              type="checkbox"
              className="code-checkbox"
              id={`checkbox_${idPrefix}_${index}`}
              checked={code.isSelected || false}
              onChange={(e) => onCodeSelectionChange && onCodeSelectionChange(code.codeId, e.target.checked)}
              disabled={!canEdit}
            />
          </div>
        </div>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  return prevProps.code === nextProps.code &&
         prevProps.editingCodeId === nextProps.editingCodeId &&
         prevProps.editableDescription === nextProps.editableDescription &&
         prevProps.canEdit === nextProps.canEdit &&
         prevProps.shouldEnableSignificanceToggle === nextProps.shouldEnableSignificanceToggle &&
         prevProps.qofContent === nextProps.qofContent &&
         prevProps.onQuoteClick === nextProps.onQuoteClick &&
         prevProps.onDelete === nextProps.onDelete &&
         prevProps.onDateChange === nextProps.onDateChange &&
         prevProps.highlighted === nextProps.highlighted &&
         prevProps.durationDropdownOpen?.[prevProps.code?.codeId] === nextProps.durationDropdownOpen?.[nextProps.code?.codeId];
});

// Reusable codes list component
export const CodesList = React.memo(({ codes, title, idPrefix, onSelectAll, onCodeSelectionChange, onSignificanceChange, onDurationChange, onDescriptionEdit, onDescriptionSave, editingCodeId, editableDescription, setEditableDescription, canEdit, durationDropdownOpen, toggleDurationDropdown, getDurationDescription, getDurationOptions, canEditDuration = true, shouldEnableSignificanceToggle = true, description, onQuoteClick = null, onDelete = null, onDateChange = null, highlightedCodeId = null }) => (
  <div>
    <div className="suggestion-header">
      <h4>{title}</h4>
      {codes.length > 0 && canEdit && (
        <input 
          type="checkbox" 
          className="code-checkbox" 
          onChange={onSelectAll}
          disabled={!canEdit}
        />
      )}
    </div>
    {description && (
      <div className="tile-header-content">
        <p style={{ fontSize: '0.9em', color: '#666', fontStyle: 'italic' }}>
          {description}
        </p>
      </div>
    )}
    <div className="document-codes-list">
      {codes.length === 0 ? (
        <EmptyStateMessage message={`No ${title.toLowerCase()} found`} />
      ) : (
        codes.map((code, index) => (
          <CodeItem 
            key={code.codeId || index} 
            code={code} 
            index={index} 
            idPrefix={idPrefix}
            onCodeSelectionChange={onCodeSelectionChange}
            onSignificanceChange={onSignificanceChange}
            onDurationChange={onDurationChange}
            onDescriptionEdit={onDescriptionEdit}
            onDescriptionSave={onDescriptionSave}
            editingCodeId={editingCodeId}
            editableDescription={editableDescription}
            setEditableDescription={setEditableDescription}
            canEdit={canEdit}
            durationDropdownOpen={durationDropdownOpen}
            toggleDurationDropdown={toggleDurationDropdown}
            getDurationDescription={getDurationDescription}
            getDurationOptions={getDurationOptions}
            canEditDuration={canEditDuration}
            shouldEnableSignificanceToggle={shouldEnableSignificanceToggle}
            onQuoteClick={onQuoteClick}
            onDelete={onDelete}
            onDateChange={onDateChange}
            highlighted={highlightedCodeId != null && code.codeId === highlightedCodeId}
          />
        ))
      )}
    </div>
  </div>
));
